import { debounce } from 'lodash';
import React from 'react';
import { useWrapRequest } from '../../../components/hooks/use-wrap-request';
import { useSearchReducer } from '../../../components/hooks/useSearchReducer';
import { Api } from '../../../core/api';
import { injectTSDI } from '../../../core/tsdi';
import {
    RecurrenceType,
    UserGoalAssignmentAdminDto as ApiUserGoalAssignmentAdminDto,
    GoalAssignmentStatus,
    GoalOwnerDto,
    GoalProgressDto
} from '../../../core/api/FitnessActivityClient';

export type { GoalAssignmentStatus, GoalOwnerDto, GoalProgressDto };

// Simplified badge type info for goal display
export interface LinkedBadgeTypeSummary {
    id: number;
    key: string;
    name: Record<string, string>;
}

// User goal assignment data for display purposes
export interface UserGoalAssignmentDto {
    id: number;
    key: string;
    owner?: GoalOwnerDto;
    recurrence: RecurrenceType;
    dayFrom?: string;
    dayTo?: string;
    assignmentId?: number;
    assignmentDayFrom?: string;
    assignmentDayTo?: string;
    progress?: number;
    status?: GoalAssignmentStatus;
    linkedBadgeTypes: LinkedBadgeTypeSummary[];
}

// Admin DTO from the support API
export interface UserGoalAssignmentAdminDto {
    assignmentId: number;
    goalId: number;
    goalKey: string;
    userId: string;
    recurrence: string;
    dayFrom?: string;
    dayTo?: string;
    progress?: number;
    status?: GoalAssignmentStatus;
    linkedBadgeTypes?: { id: number; name: Record<string, string> }[];
    createdDate: string;
    modifiedDate: string;
}

interface UserGoalsSearchParams {
    page?: number;
    size?: number;
    search?: string;
    sort?: string;
    type?: RecurrenceType;
    hasBadge?: boolean;
}

type HasBadgeFilter = 'all' | 'withBadge' | 'withoutBadge';

export type UserGoalAssignmentDetailsDto = ApiUserGoalAssignmentAdminDto;

export function useUserGoalsStore(userId: string) {
    const { fitnessActivityClient } = injectTSDI(Api);

    const [hasBadgeFilter, setHasBadgeFilter] =
        React.useState<HasBadgeFilter>('all');

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });

    const setSearchTerm = React.useMemo(
        () =>
            debounce((search?: string) => {
                dispatch({ type: 'search', search: search || '' });
            }, 300),
        []
    );

    // Map API DTO to local UserGoalAssignmentDto
    const mapToUserGoalAssignment = (
        dto: ApiUserGoalAssignmentAdminDto
    ): UserGoalAssignmentDto => {
        const progressPercent =
            dto.currentProgress && dto.goalTargetValue
                ? Math.round(
                      (Number(dto.currentProgress) /
                          Number(dto.goalTargetValue)) *
                          100
                  )
                : 0;

        return {
            id: dto.goalId,
            key: dto.goalKey,
            owner: dto.goalOwner,
            recurrence: dto.goalRecurrence,
            dayFrom: dto.dayFrom,
            dayTo: dto.dayTo,
            assignmentId: Number(dto.id),
            assignmentDayFrom: dto.dayFrom,
            assignmentDayTo: dto.dayTo,
            progress: Math.min(progressPercent, 100),
            status: dto.status,
            linkedBadgeTypes:
                dto.linkedBadgeTypes?.map((bt) => ({
                    id: bt.badgeTypeId,
                    key: bt.badgeTypeKey,
                    name: bt.badgeTypeName
                })) || []
        };
    };

    const userGoals = useWrapRequest(
        async (params?: UserGoalsSearchParams) => {
            const hasBadge =
                hasBadgeFilter === 'withBadge'
                    ? true
                    : hasBadgeFilter === 'withoutBadge'
                      ? false
                      : undefined;
            const query = {
                page: params?.page || 0,
                size: params?.size || 100,
                hasBadge: params?.hasBadge ?? hasBadge
            };

            const response =
                await fitnessActivityClient.admin.userGoalAdminControllerGetUserGoalAssignments(
                    userId,
                    query
                );

            const goals = response.data.content.map(mapToUserGoalAssignment);
            return goals;
        },
        {
            defaultData: []
        }
    );

    const userGoalDetails = useWrapRequest(async (assignmentId: number) => {
        const response =
            await fitnessActivityClient.admin.userGoalAdminControllerGetUserGoalAssignment(
                userId,
                assignmentId
            );
        return response.data;
    });

    const userGoalProgresses = useWrapRequest(
        async (assignmentId: number) => {
            const response =
                await fitnessActivityClient.admin.goalProgressSupportControllerGetGoalProgresses(
                    assignmentId,
                    {
                        page: 0,
                        size: 100,
                        sort: '-day'
                    }
                );
            return response.data.content;
        },
        {
            defaultData: []
        }
    );

    const loadUserGoals = React.useCallback(
        async (params?: Partial<UserGoalsSearchParams>) => {
            await userGoals.request([
                {
                    ...searchParams,
                    ...params
                }
            ]);
        },
        [userGoals, searchParams, hasBadgeFilter]
    );

    // Reload goals when filter changes
    React.useEffect(() => {
        loadUserGoals();
    }, [hasBadgeFilter, loadUserGoals]);

    const unassignGoal = React.useCallback(
        async (assignmentId: number) => {
            await fitnessActivityClient.admin.userGoalAdminControllerDeleteUserGoalAssignment(
                userId,
                assignmentId
            );
        },
        [fitnessActivityClient, userId]
    );

    const refreshGoalProgressStreak = React.useCallback(
        async (goalProgressId: number) => {
            await fitnessActivityClient.admin.goalProgressSupportControllerRefreshStreak(
                goalProgressId
            );
        },
        [fitnessActivityClient]
    );

    const refreshGoalProgress = React.useCallback(
        async (goalProgressId: number) => {
            await fitnessActivityClient.admin.goalProgressSupportControllerRefreshProgress(
                goalProgressId
            );
        },
        [fitnessActivityClient]
    );

    return {
        userGoals,
        setSearchTerm,
        searchParams,
        dispatch,
        hasBadgeFilter,
        setHasBadgeFilter,
        loadUserGoals,
        userGoalDetails,
        userGoalProgresses,
        unassignGoal,
        refreshGoalProgressStreak,
        refreshGoalProgress
    };
}
